import { useCallback, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { UnicornApi } from '@cld/unicorn-api';
import { API_BASE_URL } from '@cld-remote/application_wrapper/index';

import { createApiClient } from './api/createApiClient';
import { RoutesGuard } from './components';
import { DeveloperGettingStarted } from './routes/developer-getting-started';
import MediaExplorer from './routes/media-explorer/media-explorer';
import { MediaLibrary } from './routes/media-library';
import { IframeModule } from './components/iframe';
import { Loader } from './components/loader';
import { RoutesEffects } from './components/routes-effects/routes-effects';
import { useMessage } from './i18n/useMessage';
import { DeveloperDashboard } from './routes/developer-dashboard';
import { DeliveryReports } from './routes/delivery-reports';
import { ErrorReports } from './routes/error-reports';
import { MonthlyValueReports } from './routes/monthly-value-reports';
import { TransformationsRouting } from './routes/transformations/TransformationsRouting';
import { PageNotFound } from './routes/page-not-found';
import { createLegacyUrl } from './utils/legacy';
import { createConsoleUrl } from './utils/console';
import { OptimizationReports } from './routes/optimization-reports';
import { OptimizationCenter } from './routes/optimization-center';
import { UsageReports } from './routes/usage-reports';
import { Welcome } from './routes/welcome';
import { useAppData } from './context/app-context';
import TransformationSuite from './routes/transformation-suite/transformation-suite';
import { Marketplace } from './routes/maketplace';
import { VideoLinkRedirect } from './routes/video-link-redirect';
import { Taco } from './routes/transformation-suite/taco';
import { TreatmentValue } from './types/treatments.js';

interface AppConnectedProps {
  unicornApi: UnicornApi;
  mediaExplorerDeprecationTreatment: TreatmentValue;
}

export const AppConnected = ({ unicornApi, mediaExplorerDeprecationTreatment }: AppConnectedProps) => {
  const { productEnvironment } = unicornApi.data;

  const shouldDisplayMediaExplorer = mediaExplorerDeprecationTreatment === 'off';

  const t = useMessage();
  // FIXME: later it should be used from unicorn once available, to use provided auth
  const apiClient = useMemo(() => createApiClient({ httpClient: fetch, baseClientUrl: API_BASE_URL }), []);

  const navigateOutsideApplication = useCallback((path: string) => {
    window.location.href = path;
  }, []);

  const { features, programmableMediaLogger } = useAppData();
  const {
    isAllowedForReports,
    isAllowedForVideoPages,
    isAllowedForNewVideoApp,
    isNewErrorReportsPageEnabled,
    isNewVideoReportsPageEnabled,
    isAllowedForMonthlyValueReport,
    isAllowedForOnboarding,
    shouldRedirectToOnboarding,
    shouldDisplayThumbnails,
    shouldDisplayMarketplace,
  } = features;

  if (isAllowedForOnboarding && shouldRedirectToOnboarding) {
    navigateOutsideApplication(createConsoleUrl('welcome'));
    return <Loader />;
  }

  // TODO: Get rid of `ApplicationRouter` in favor of top level routes defined via `createBrowserRouter`
  return (
    <programmableMediaLogger.ErrorBoundary errorBoundaryLogLabel="app-connected" data-test="app-connected-error">
      <RoutesEffects cloudName={unicornApi.data.productEnvironment.cloudName} />
      <RoutesGuard features={features}>
        <Routes>
          <Route index element={<Navigate to="developer-dashboard" replace />} />
          <Route path="welcome" element={<Welcome />} />
          <Route
            path="developer-dashboard"
            element={
              features ? (
                <DeveloperDashboard
                  apiClient={apiClient}
                  navigateOutsideApplication={navigateOutsideApplication}
                  usedNewDashboardBeforeFullRelease={features.usedNewDashboardBeforeFullRelease}
                  unicornApi={unicornApi}
                />
              ) : (
                <Loader />
              )
            }
          />
          {shouldDisplayMediaExplorer && (
            <Route path="media-explorer/*" element={<MediaExplorer unicornApi={unicornApi} shouldDisplayThumbnails={shouldDisplayThumbnails} />} />
          )}
          <Route path="media_library/*" element={<MediaLibrary />} />
          <Route path="getting-started/*" element={<DeveloperGettingStarted unicornApi={unicornApi} />} />
          <Route
            path="transformation_builder/*"
            element={<TransformationSuite unicornApi={unicornApi} shouldDisplayThumbnails={shouldDisplayThumbnails} />}
          />
          <Route path="taco/*" element={<Taco unicornApi={unicornApi} shouldDisplayThumbnails={shouldDisplayThumbnails} />} />
          <Route
            path="transformations/*"
            element={<TransformationsRouting unicornApi={unicornApi} navigateOutsideApplication={navigateOutsideApplication} />}
          />
          <Route path="upgrade_options" element={<IframeModule src={createLegacyUrl('/upgrade_options', productEnvironment.externalId)} />} />
          <Route path="optimization-center" element={<OptimizationCenter unicornApi={unicornApi} />} />
          <Route path="optimization-reports" element={<OptimizationReports />} />

          {isAllowedForVideoPages && isAllowedForNewVideoApp && (
            <>
              <Route path="video_hub" element={<VideoLinkRedirect modulePath="home" />} />
              <Route path="video-player-profiles" element={<VideoLinkRedirect modulePath="player-profiles" />} />
              <Route path="video-player-studio/*" element={<VideoLinkRedirect modulePath="player-studio" />} />
              {isAllowedForReports && isNewVideoReportsPageEnabled && (
                <Route path="reports/video-player" element={<VideoLinkRedirect modulePath="analytics" />} />
              )}
            </>
          )}

          {isAllowedForReports && (
            <>
              {features && !features.usedNewDashboardBeforeFullRelease && (
                <Route path="reports/usage" element={<UsageReports unicornApi={unicornApi} />} />
              )}
              <Route path="reports/delivery" element={<DeliveryReports unicornApi={unicornApi} />} />
              <Route path="reports" element={<Navigate to="./delivery" replace />} />
              {isNewErrorReportsPageEnabled ? (
                <Route path="reports/errors" element={<ErrorReports />} />
              ) : (
                <Route path="errors" element={<IframeModule src={createLegacyUrl('/errors', productEnvironment.externalId)} title={t('reports')} />} />
              )}
              {isAllowedForMonthlyValueReport && <Route path="reports/monthly-value" element={<MonthlyValueReports />} />}
              {shouldDisplayMarketplace && <Route path="marketplace/*" element={<Marketplace unicornApi={unicornApi} />} />}
            </>
          )}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </RoutesGuard>
    </programmableMediaLogger.ErrorBoundary>
  );
};
